/**
 * LIVE READS — one pattern, used everywhere.
 *
 * The bug this exists to kill, in the owner's words: *"the game needs to load
 * some pages still like the tables, where i added someone and messages had to
 * reload the page to show."* He was right, and it was not a Tables bug — it was
 * a whole CLASS of bug. Ten screens fetched once on mount and then sat there
 * being confidently wrong: your table's roster, its thread, the Daily board, the
 * leaderboards, your own stats. Anything another person could change while you
 * were looking at it, you had to press F5 to see.
 *
 * WHY FIRESTORE AND NOT SOCKETS. The truth for all of it already lives in
 * Firestore, and `firestore.rules` already lets the client read it —
 * `firestore.rules:264` says, in as many words, *"Readable by the table's
 * members, so the client can live-subscribe."* Someone anticipated this and the
 * client simply never did it. So there is no new socket event, no new backend
 * route, and no polling: we subscribe to the documents that are already the
 * source of truth. Sockets keep the job they are actually good at — EPHEMERAL
 * room state (who is in the lobby right now, the live game) that is not in
 * Firestore and never should be.
 *
 * READS BECOME LIVE. WRITES DO NOT CHANGE. Every mutation still goes through the
 * REST API, because the server owns the profanity screen, the rate limits, the
 * back-fill and the system cards. The rules deny client writes to `tables`,
 * `thread`, `dailyBoards` and `gameRecords` ON PURPOSE. If a change here ever
 * seems to need those rules loosened, the change is wrong.
 *
 * ---------------------------------------------------------------------------
 * THE TWO THINGS THAT MAKE THIS SAFE TO USE EVERYWHERE
 *
 * 1. IDENTITY. A Firestore `Query` is a fresh object on every render, so the
 *    obvious `useEffect(..., [q])` resubscribes forever — a listener leak that
 *    is also a BILLING leak, because every resubscribe re-reads the whole result
 *    set. We compare with Firestore's own `queryEqual` / `refEqual` and keep the
 *    previous object identity whenever the query is semantically the same, so a
 *    caller can build its query inline (the readable way) and still subscribe
 *    exactly once.
 *
 * 2. DETACHMENT. The cleanup unsubscribes AND flips a `live` flag, so a snapshot
 *    that is already in flight when a component unmounts cannot call `setState`
 *    on a corpse. `liveListenerCount()` exposes the number of open listeners,
 *    which is what the tests assert on: mount N, unmount, expect 0.
 *
 * ON DETACHING WHEN THE TAB IS HIDDEN — considered, and deliberately NOT done.
 * An attached listener bills only for documents that CHANGE. Re-attaching bills
 * for the entire result set again. A user who alt-tabs ten times would pay ten
 * full re-reads of the board to save nothing, and would watch the list flicker
 * each time they came back. Staying attached is both cheaper and correct.
 */

import { useEffect, useRef, useState } from 'react';
import {
  onSnapshot,
  queryEqual,
  refEqual,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type FirestoreError,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore';

export interface LiveResult<T> {
  data: T;
  /** True until the first snapshot lands. A null query is never loading. */
  loading: boolean;
  /**
   * The listener was REJECTED. This matters: a board that is empty because the
   * read was denied looks exactly like a board that is empty because nobody has
   * played, and the two must never be shown the same way.
   */
  error: FirestoreError | null;
}

/* ----------------------------------------------------------- listener census */

let openListeners = 0;

/** How many Firestore listeners this app currently holds open. Used by the
 *  tests to prove nothing leaks, and handy in the console when auditing cost. */
export function liveListenerCount(): number {
  return openListeners;
}

/**
 * The census, on `window`, so the cost of any screen can be read without a
 * debugger: open a page, type `__wrLiveListeners()`, navigate away, type it
 * again. It should go back down. A number that only ever climbs is a leak, and
 * this is the cheapest possible way to see one.
 *
 * Not dev-gated, deliberately — it exposes a COUNT and nothing else (no data, no
 * queries, no uids), it is what the two-browser verification reads, and a
 * diagnostic that only exists in dev is a diagnostic you cannot use on the day
 * production is the thing behaving strangely.
 */
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__wrLiveListeners = liveListenerCount;
}

/* --------------------------------------------------------- stable identities */

/**
 * Keep the PREVIOUS object whenever the next one means the same thing.
 *
 * This is the whole reason a caller can write `useLiveDoc(tableRef(id), …)`
 * inline without re-subscribing on every keystroke elsewhere in the component.
 * Mutating a ref during render is safe here because the operation is idempotent:
 * equal in, same object out.
 */
function useStable<T>(next: T | null, eq: (a: T, b: T) => boolean): T | null {
  const held = useRef<T | null>(null);
  if (next === null) {
    held.current = null;
  } else if (held.current === null || !eq(held.current, next)) {
    held.current = next;
  }
  return held.current;
}

/* -------------------------------------------------------------------- hooks */

/**
 * Live-subscribe to a QUERY. Pass `null` to subscribe to nothing (not signed in
 * yet, no id yet) — the hook then holds no listener at all.
 *
 * `map` is read from a ref, so an inline arrow does not resubscribe. It is
 * expected to be a pure snapshot → rows function; if the mapping itself needs to
 * change, change the query.
 */
export function useLiveQuery<T>(
  q: Query<DocumentData> | null,
  map: (snap: QuerySnapshot<DocumentData>) => T[],
): LiveResult<T[]> {
  const stableQuery = useStable(q, queryEqual);
  const mapRef = useRef(map);
  mapRef.current = map;

  const [state, setState] = useState<LiveResult<T[]>>({
    data: [],
    loading: stableQuery !== null,
    error: null,
  });

  useEffect(() => {
    if (!stableQuery) {
      setState({ data: [], loading: false, error: null });
      return;
    }

    let live = true;
    setState(s => ({ data: s.data, loading: true, error: null }));

    openListeners += 1;
    const unsub = onSnapshot(
      stableQuery,
      snap => {
        if (!live) return;
        setState({ data: mapRef.current(snap), loading: false, error: null });
      },
      err => {
        if (!live) return;
        // Denied is not empty. Say so, and let the screen decide what to show.
        console.warn('[useLiveQuery] snapshot error:', err?.code || err?.message || err);
        setState({ data: [], loading: false, error: err });
      },
    );

    return () => {
      live = false;
      openListeners -= 1;
      unsub();
    };
  }, [stableQuery]);

  return state;
}

/**
 * Live-subscribe to SEVERAL queries and merge them into one list.
 *
 * This exists for exactly one shape of problem: a set of documents too large for
 * a single Firestore `in` filter, which caps at 30. The Daily's FAMILY slice is
 * that shape — everyone at all of my tables, asked for by document id. A real
 * family is one chunk and therefore ONE listener; the plural form is what stops
 * somebody who sits at five big tables from silently losing the 31st person.
 *
 * `loading` stays true until every chunk has reported, so the board never paints
 * half a family and then jumps.
 */
export function useLiveQueries<T>(
  queries: Array<Query<DocumentData>>,
  map: (snap: QuerySnapshot<DocumentData>) => T[],
): LiveResult<T[]> {
  const stable = useStableList(queries);
  const mapRef = useRef(map);
  mapRef.current = map;

  const [state, setState] = useState<LiveResult<T[]>>({
    data: [],
    loading: stable.length > 0,
    error: null,
  });

  useEffect(() => {
    if (stable.length === 0) {
      setState({ data: [], loading: false, error: null });
      return;
    }

    let live = true;
    const parts: T[][] = stable.map(() => []);
    const reported = new Set<number>();
    setState(s => ({ data: s.data, loading: true, error: null }));

    const unsubs = stable.map((q, i) => {
      openListeners += 1;
      return onSnapshot(
        q,
        snap => {
          if (!live) return;
          parts[i] = mapRef.current(snap);
          reported.add(i);
          setState({
            data: parts.flat(),
            loading: reported.size < stable.length,
            error: null,
          });
        },
        err => {
          if (!live) return;
          console.warn('[useLiveQueries] snapshot error:', err?.code || err?.message || err);
          setState({ data: [], loading: false, error: err });
        },
      );
    });

    return () => {
      live = false;
      openListeners -= unsubs.length;
      unsubs.forEach(u => u());
    };
  }, [stable]);

  return state;
}

/** The array form of `useStable`. Same query list in, same array identity out. */
function useStableList(next: Array<Query<DocumentData>>): Array<Query<DocumentData>> {
  const held = useRef<Array<Query<DocumentData>>>([]);
  const same =
    held.current.length === next.length &&
    held.current.every((q, i) => queryEqual(q, next[i]));
  if (!same) held.current = next;
  return held.current;
}

/**
 * Live-subscribe to a single DOCUMENT. `data` is null when the doc does not
 * exist — which, for a table, is also what "you were removed from it" looks like.
 */
export function useLiveDoc<T>(
  ref: DocumentReference<DocumentData> | null,
  map: (snap: DocumentSnapshot<DocumentData>) => T | null,
): LiveResult<T | null> {
  const stableRef = useStable(ref, refEqual);
  const mapRef = useRef(map);
  mapRef.current = map;

  const [state, setState] = useState<LiveResult<T | null>>({
    data: null,
    loading: stableRef !== null,
    error: null,
  });

  useEffect(() => {
    if (!stableRef) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    let live = true;
    setState(s => ({ data: s.data, loading: true, error: null }));

    openListeners += 1;
    const unsub = onSnapshot(
      stableRef,
      snap => {
        if (!live) return;
        setState({ data: snap.exists() ? mapRef.current(snap) : null, loading: false, error: null });
      },
      err => {
        if (!live) return;
        console.warn('[useLiveDoc] snapshot error:', err?.code || err?.message || err);
        setState({ data: null, loading: false, error: err });
      },
    );

    return () => {
      live = false;
      openListeners -= 1;
      unsub();
    };
  }, [stableRef]);

  return state;
}
